import { useMediaQuery } from "@mantine/hooks"
import { cn } from "app/lib/utils"
import { useAtomValue, useSetAtom } from "jotai"
import { type MouseEvent, type MouseEventHandler, useCallback } from "react"
import {
  isFinishedAtom,
  isPlayingAtom,
  isPreventedAtom,
} from "app/player/state"

export type PlayToggleState = "play" | "pause" | "replay"

type Options = {
  onClick?: MouseEventHandler<HTMLButtonElement>
  onReplayClick?: MouseEventHandler<HTMLButtonElement>
  onMouseDown?: MouseEventHandler<HTMLButtonElement>
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>
  className?: string
}

export const isPlayButton = (element: EventTarget) => {
  return (
    element instanceof Element && element.closest(".playingButton") !== null
  )
}

/**
 * Shared play/pause/replay state and prevent-hide handlers for concrete
 * play button components.
 */
export default function usePlayToggleControls({
  onClick,
  onReplayClick,
  onMouseDown,
  onMouseLeave,
  className,
}: Options = {}) {
  const setPrevented = useSetAtom(isPreventedAtom)
  const isPlaying = useAtomValue(isPlayingAtom)
  const isTouch = useMediaQuery("(pointer: coarse)")
  const isFinished = useAtomValue(isFinishedAtom)

  const state: PlayToggleState = isFinished
    ? "replay"
    : isPlaying
      ? "pause"
      : "play"

  const handleClick = state === "replay" ? (onReplayClick ?? onClick) : onClick

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (state === "replay") {
        onMouseDown?.(e)
        return
      }
      if (!isTouch) {
        setPrevented(true)
      }
      onMouseDown?.(e)
    },
    [state, isTouch, setPrevented, onMouseDown]
  )

  const handleMouseLeave = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (state === "replay") {
        onMouseLeave?.(e)
        return
      }
      setPrevented(false)
      onMouseLeave?.(e)
    },
    [state, setPrevented, onMouseLeave]
  )

  return {
    state,
    buttonProps: {
      className: cn(state !== "replay" && "playingButton", className),
      onClick: handleClick,
      onMouseDown: handleMouseDown,
      onMouseLeave: handleMouseLeave,
    },
  }
}
